import PropTypes from 'prop-types';
import { LinkButton, Text, textVariants } from '@etg/wings';
import { ExpandableContent, Icon } from '@eti/components';
import { price as priceUtil, constants } from '@eti/utils';
import { css, cx, etiColors } from '@eti/styles';
import { useToggle } from '../../../common/custom-hooks/toggle';
import useId from '../hooks/useId';
import { CartRow, Price } from './CartComponents';

const priceBreakdownStyles = css`
  color: ${etiColors.grayScale[10]};
  font-size: 0.75rem;
`;

const chevronIconStyles = (isExpanded) => css`
  margin-top: 2px;
  transform: rotateX(${isExpanded ? '180deg' : '0deg'});
  transition: transform 0.2s ease-in-out;
`;

const toggleStyles = css`
  align-items: center;
  display: flex;
  gap: 6px;
`;

export const RowToggle = ({ children, dataTestId, id, isExpanded, onClick }) => (
  <LinkButton
    aria-controls={id}
    aria-expanded={isExpanded}
    className={toggleStyles}
    data-testid={dataTestId}
    onClick={onClick}
  >
    <span>{children}</span>
    <Icon
      className={chevronIconStyles(isExpanded)}
      color="currentColor"
      id="chevron-down"
      size="0.6875rem"
    />
  </LinkButton>
);

RowToggle.propTypes = {
  children: PropTypes.node,
  dataTestId: PropTypes.string,
  id: PropTypes.string,
  isExpanded: PropTypes.bool,
  onClick: PropTypes.func,
};

const CartToggleableRow = ({ data, dataTestId, price, title }) => {
  const { isOpen, handleToggle } = useToggle(false);
  const uniqueId = useId();

  return (
    <div>
      <CartRow data-testid={dataTestId}>
        {data ? (
          <RowToggle
            dataTestId={`${dataTestId}-toggle`}
            id={uniqueId}
            isExpanded={isOpen}
            onClick={handleToggle}
          >
            {title}
          </RowToggle>
        ) : (
          <Text>{title}</Text>
        )}
        {price && <Price data-testid={`${dataTestId}-price`}>{priceUtil.formatPrice(price)}</Price>}
      </CartRow>
      <ExpandableContent isExpanded={data && isOpen}>
        {data?.map((row, index) => {
          const key = `subsection-${index}`;
          return (
            <CartRow
              className={cx(priceBreakdownStyles, constants.MOUSE_FLOW_CLASS_NAMES)}
              data-testid={`${dataTestId}-dataRow`}
              key={key}
            >
              <Text variant={textVariants._12}>{row.label || row.name}</Text>
              {row.value && (
                <Price data-testid={`${dataTestId}-dataRow-price`}>
                  {priceUtil.formatPrice(row.value)}
                </Price>
              )}
            </CartRow>
          );
        })}
      </ExpandableContent>
    </div>
  );
};

CartToggleableRow.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ),
  dataTestId: PropTypes.string,
  price: PropTypes.number,
  title: PropTypes.string,
};

export default CartToggleableRow;
